/**
 * Латинские слова, набранные с русскими буквами-двойниками.
 *
 * ⚠️ ЗАЧЕМ. `strawberr` + кириллическая «у» в наборе слов урока: ученик набирает
 * латинскую `y` и получает «неверно» при верном ответе, а кнопка «в словарь» уносит
 * кривое слово в общий словарь, где его не сдал ни один из 11 пытавшихся. На проде
 * таких блоков нашлось 23, исправлены 12.09.2026 разовым прогоном
 * (`docs/MIXED-SCRIPT-BLOCKS.md`). С того же дня сервер исправляет такие слова САМ при
 * записи блока — молча, без отказа (договор `constructor-v2.md` §5).
 *
 * ⚠️ ПОЧЕМУ ЭТО ВСЁ РАВНО НУЖНО ВО ФРОНТЕ. Сервер исправит, но методист об этом не
 * узнает — и наберёт так же в следующем задании, и в словаре, и в названии курса, куда
 * сторож не дотягивается. Подсветка нужна ровно затем, чтобы человек увидел свою
 * раскладку, а не чтобы починить данные.
 *
 * ⚠️ ТАБЛИЦЫ ЗАПИСАНЫ КОДАМИ, А НЕ БУКВАМИ. Латинская «a» на месте русской выглядит
 * так же; правка «на глаз» тихо сделала бы замену пустой, и подсветка перестала бы
 * что-либо находить, оставаясь зелёной.
 *
 * ⚠️ ЭТО НЕ `TWIN_CODES` ИЗ `wordAnswer.ts`. Там своя таблица для мягкой сверки ОТВЕТА
 * УЧЕНИКА: в ней 21 пара, включая `У→Y` и `І→I`, которых нет у сервера. Возьми мы её —
 * подсветка обещала бы исправление, которого сервер не сделает. Источник правды тут
 * один: `services/courses-service/internal/mixedscript/mixedscript.go`, согласие держит
 * `mixedScriptMatchesServer.test.ts`.
 */

/** Кириллица → латиница. Близнец `кирВЛат`. */
const TWIN_CODES: ReadonlyArray<readonly [number, string]> = [
  [0x0430, 'a'], [0x0435, 'e'], [0x043e, 'o'], [0x0440, 'p'], [0x0441, 'c'],
  [0x0443, 'y'], [0x0445, 'x'], [0x0456, 'i'],
  [0x0410, 'A'], [0x0412, 'B'], [0x0415, 'E'], [0x041a, 'K'], [0x041c, 'M'],
  [0x041d, 'H'], [0x041e, 'O'], [0x0420, 'P'], [0x0421, 'C'], [0x0422, 'T'], [0x0425, 'X'],
];

/**
 * Латинские буквы, у которых есть кириллический двойник. Близнец `латДвойники`.
 * Слово из одних только этих букв НЕ доказывает, что оно латинское.
 */
const LATIN_TWIN_CODES: ReadonlyArray<number> = [
  0x61, 0x63, 0x65, 0x69, 0x6f, 0x70, 0x78, 0x79,
  0x41, 0x42, 0x43, 0x45, 0x48, 0x49, 0x4b, 0x4d, 0x4f, 0x50, 0x54, 0x58,
];

const TWINS = new Map(TWIN_CODES.map(([cp, lat]) => [String.fromCodePoint(cp), lat]));
const LATIN_TWINS = new Set(LATIN_TWIN_CODES.map((cp) => String.fromCodePoint(cp)));

const isLatin = (ch: string) => /\p{Script=Latin}/u.test(ch);
const isCyrillic = (ch: string) => /\p{Script=Cyrillic}/u.test(ch);
/** Часть слова — буква или надстрочный знак (близнец `частьСлова`). */
const isWordPart = (ch: string) => /\p{L}/u.test(ch) || /\p{Mn}/u.test(ch);

/** Что делать со словом. Значения совпадают с серверными. */
export type Verdict =
  /** трогать нечего */
  | 'нет'
  /** бесспорно латинское — сервер заменит двойники сам */
  | 'исправить'
  /** русское или казахское слово с латинской буквой — решает методист */
  | 'латиница-в-русском'
  /** не понять, чьё слово — решает методист */
  | 'неясно';

export type Finding = {
  /** Путь внутри содержимого блока, например `words[0].word`. */
  path: string;
  was: string;
  becomes: string;
  verdict: Verdict;
};

/**
 * Разбор одного слова — дословно `Слово()` сервера.
 *
 * Исправляется, только если слово бесспорно латинское: есть латинская буква БЕЗ
 * двойника (`s`, `t`, `w`, `m`…) и вся кириллица в нём — из таблицы двойников.
 */
export function wordVerdict(word: string): { becomes: string; verdict: Verdict } {
  let latin = 0;
  let cyr = 0;
  let realLatin = false;
  let realCyr = false;
  for (const ch of word) {
    if (isLatin(ch)) {
      latin += 1;
      if (!LATIN_TWINS.has(ch)) realLatin = true;
    } else if (isCyrillic(ch)) {
      cyr += 1;
      if (!TWINS.has(ch)) realCyr = true;
    }
  }
  if (latin === 0 || cyr === 0) return { becomes: word, verdict: 'нет' };
  if (realLatin && !realCyr) {
    return { becomes: Array.from(word, (ch) => TWINS.get(ch) ?? ch).join(''), verdict: 'исправить' };
  }
  if (realCyr && !realLatin) return { becomes: word, verdict: 'латиница-в-русском' };
  return { becomes: word, verdict: 'неясно' };
}

/**
 * Разбор строки. Слова делятся по всему, что не буква: пробел, дефис, скобки, `/`,
 * `[[…]]`, цифры. Поэтому «flat-квартира» — два чистых слова, и трогать их не за что.
 */
export function scanText(text: string): { becomes: string; findings: Omit<Finding, 'path'>[] } {
  const findings: Omit<Finding, 'path'>[] = [];
  let out = '';
  let word = '';
  const flush = () => {
    if (word === '') return;
    const { becomes, verdict } = wordVerdict(word);
    if (verdict !== 'нет') findings.push({ was: word, becomes, verdict });
    out += becomes;
    word = '';
  };
  for (const ch of text) {
    if (isWordPart(ch)) {
      word += ch;
      continue;
    }
    flush();
    out += ch;
  }
  flush();
  return { becomes: out, findings };
}

/** Обход содержимого блока: только строковые ЗНАЧЕНИЯ, ключи не трогаются. */
function walk(v: unknown, path: string, findings: Finding[]): unknown {
  if (typeof v === 'string') {
    const { becomes, findings: found } = scanText(v);
    found.forEach((f) => findings.push({ ...f, path }));
    return becomes;
  }
  if (Array.isArray(v)) return v.map((e, i) => walk(e, `${path}[${i}]`, findings));
  if (v && typeof v === 'object') {
    const src = v as Record<string, unknown>;
    const out: Record<string, unknown> = {};
    // Ключи по порядку — как у сервера, чтобы находки шли в том же порядке.
    for (const k of Object.keys(src).sort()) {
      out[k] = walk(src[k], path === '' ? k : `${path}.${k}`, findings);
    }
    return out;
  }
  return v;
}

/** Что сервер найдёт в содержимом блока. Порядок — как у него. */
export function scanContent(content: unknown): Finding[] {
  const findings: Finding[] = [];
  walk(content, '', findings);
  return findings;
}

/**
 * Содержимое с исправленными бесспорными словами — ровно то, что запишет сервер.
 *
 * ⚠️ Возвращает ИСХОДНЫЙ объект, если исправлять нечего: иначе каждое сохранение
 * подсовывало бы новый объект, снимок «менялось ли» расходился бы всегда, и мы
 * вернулись бы к запросу на каждый блок урока.
 */
export function applyFixes<T>(content: T): T {
  const findings: Finding[] = [];
  const next = walk(content, '', findings);
  return findings.some((f) => f.verdict === 'исправить') ? (next as T) : content;
}

/**
 * Слово, разобранное на куски для показа: какие буквы кириллические.
 *
 * ⚠️ БЕЗ ЭТОГО ПЛАШКА БЕСПОЛЕЗНА. «strawberrу → strawberry» на экране выглядит как
 * «strawberry → strawberry»: буквы неразличимы, и методист решает, что придираются на
 * пустом месте. Подсветить нужно саму букву — договор §5.2 требует показать её и то,
 * что встанет вместо неё.
 */
export function splitTwins(word: string): { text: string; twin: boolean; becomes?: string }[] {
  const out: { text: string; twin: boolean; becomes?: string }[] = [];
  for (const ch of word) {
    const lat = TWINS.get(ch);
    if (lat !== undefined) {
      out.push({ text: ch, twin: true, becomes: lat });
    } else if (out.length > 0 && !out[out.length - 1].twin) {
      out[out.length - 1].text += ch;
    } else {
      out.push({ text: ch, twin: false });
    }
  }
  return out;
}

/** Код буквы для показа: «у» неотличима глазом, а `U+0443` — отличима. */
export const codeOf = (ch: string) => `U+${(ch.codePointAt(0) ?? 0).toString(16).toUpperCase().padStart(4, '0')}`;

/** Только то, что сервер исправит сам. */
export const willBeFixed = (findings: Finding[]) => findings.filter((f) => f.verdict === 'исправить');

/** Только то, что сервер НЕ тронет: решает методист. */
export const needsHumanLook = (findings: Finding[]) => findings.filter((f) => f.verdict !== 'исправить');
